import { createAsyncThunk } from "@reduxjs/toolkit"
import { GenericRejectionResponseWithReason } from "integrations/redux/types"
import { ID } from "@nova/constants"
import { RootState } from "integrations/redux/rootReducer"
import { modifyHiddenLayerList } from "panels/content/side-panel/module"
import { rasterizePDF } from "plugins/PDFRaster/module"
import { fileManagementApi } from "features/data-management/files/fileManagementApi"
import { pdfRasterPath } from "@nova/util-s3"
import { selectActiveWorkspaceId } from "features/data-management/workspace/WorkspaceManagementSlice"
import { selectActivatedFireId } from "features/map/control/selectors"
import { zoomToShowPoints } from "features/map/zoom/thunks"

const isDownloadingId = new Set<string>()
export const pdfRasterImageData: Record<ID, ImageData> = {}

const imageDataFromSource = (src: string) =>
    new Promise<ImageData>((resolve, reject) => {
        const img = new Image()
        img.onload = () => {
            const canvas = document.createElement("canvas")
            canvas.width = img.width
            canvas.height = img.height
            const context = canvas.getContext("2d")!
            context.drawImage(img, 0, 0)
            resolve(context.getImageData(0, 0, canvas.width, canvas.height))
        }
        img.onerror = reject
        img.src = src
    })

export const updatePDFRasterVisibility = createAsyncThunk<
    { visible: boolean; imageData?: ImageData; pdfBlobUrl?: string },
    { id: ID; visible: boolean },
    GenericRejectionResponseWithReason
>("entities/pdfRasters/updatePDFRasterVisibility", async (payload, thunkApi) => {
    const state = thunkApi.getState() as RootState
    const orgId = selectActiveWorkspaceId()(state)
    const projectId = selectActivatedFireId(state)
    // Can't use the normal selector to get the PDFRaster or we get a circular dependency.
    const raster = state.project.data.PDFRasters.find((raster) => raster.id === payload.id)
    if (!raster) throw Error("PDF Raster didn't exist when toggling visibility.") // Should never happen

    modifyHiddenLayerList(payload.id, payload.visible)

    if (raster.tiled) return { visible: payload.visible }

    const localRaster = state["pdf-raster-ui-v1"].rasterById[payload.id] as { url?: string }

    let imageData: ImageData | undefined
    let pdfBlobUrl: string | undefined

    if (payload.visible && !pdfRasterImageData[payload.id] && !isDownloadingId.has(payload.id)) {
        isDownloadingId.add(payload.id)

        // Backwards compatibility to support when we used to store PDF rasters as JPGs instead of the PDFs themselves.
        if (localRaster.url?.toLowerCase().endsWith(".jpg")) {
            imageData = await imageDataFromSource(localRaster.url)
        } else {
            const s3Key = localRaster.url ?? pdfRasterPath(orgId!, projectId!, raster.filename)
            const buffer = await (await fileManagementApi.fetchFile(s3Key)).arrayBuffer()
            imageData = await rasterizePDF(buffer.slice(0))
            pdfBlobUrl = URL.createObjectURL(new Blob([buffer], { type: "application/pdf" }))
        }

        isDownloadingId.delete(payload.id)
        thunkApi.dispatch(
            zoomToShowPoints({
                points: raster.corners.map((corner) => [corner.longitude, corner.latitude]),
            })
        )
    }

    return { visible: payload.visible, imageData, pdfBlobUrl }
})
